<script setup lang="ts">
import { computed } from 'vue';
import type { TranscriptMessage } from '@/stores/useVoiceTranscript';
import { VoiceTranscriptStatus } from '@/common/voice-ai/voiceAiMessage';
import ChatMessageWrapper from '@/components/chat/messages/ChatMessageWrapper.vue';
import PlsText from '@/components/PlsText.vue';
import { MemberType } from '@/common/bot/enum';
import type { BaseMessage } from '@/library/message/types';
import { useI18n } from '@/plugins/i18n';

interface VoiceTranscriptProps {
  messages: TranscriptMessage[];
}

const props = defineProps<VoiceTranscriptProps>();

const { t } = useI18n();

const visibleMessages = computed(() =>
  props.messages.filter(
    (message) =>
      message.status === VoiceTranscriptStatus.Listening ||
      message.status === VoiceTranscriptStatus.Thinking ||
      message.status === VoiceTranscriptStatus.Streaming ||
      Boolean(message.spokenText)
  )
);

/**
 * Reuse the chat message bubble so the transcript matches the conversation feed
 * exactly (avatars, bubble colours, tail, typography). The bubble body is fully
 * provided via the default slot; the wrapper only consumes `memberType` (which side
 * to render) and `message` (empty here — keeps the bubble visible via the wrapper's
 * `message || !$slots.actions` guard). Other BaseMessage fields are not read, hence
 * the assertion to satisfy the prop contract.
 */
const toBaseMessage = (message: TranscriptMessage): BaseMessage =>
  ({
    memberType: message.role === 'ai' ? MemberType.AGENT : MemberType.VISITOR,
    message: '',
  }) satisfies Pick<BaseMessage, 'memberType' | 'message'> as BaseMessage;

const isThinking = (message: TranscriptMessage): boolean =>
  message.status === VoiceTranscriptStatus.Thinking ||
  (message.status === VoiceTranscriptStatus.Streaming && !message.spokenText);
</script>

<template>
  <!-- Scroll is owned by the parent overlay; this is a plain presentation component. -->
  <div
    class="voice-transcript"
    role="log"
    aria-live="polite"
    aria-label="Voice call transcript"
    data-testid="voice-transcript"
  >
    <ChatMessageWrapper
      v-for="message in visibleMessages"
      :key="message.id"
      :message="toBaseMessage(message)"
      :data-testid="`voice-transcript-row-${message.role}`"
    >
      <!-- Listening placeholder -->
      <span
        v-if="message.status === VoiceTranscriptStatus.Listening"
        class="theme-font-main voice-transcript__placeholder"
        data-testid="voice-transcript-listening"
      >
        {{ t('page.voice.transcript.listening') }}
      </span>

      <!-- Thinking placeholder (also covers a streaming bubble before its first character) -->
      <span
        v-else-if="isThinking(message)"
        class="theme-font-main voice-transcript__placeholder"
        data-testid="voice-transcript-thinking"
      >
        {{ t('page.voice.transcript.thinking') }}
      </span>

      <!-- Revealed transcript text -->
      <PlsText v-else>{{ message.spokenText }}</PlsText>
    </ChatMessageWrapper>
  </div>
</template>

<style scoped lang="scss">
.voice-transcript {
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
}

.voice-transcript__placeholder {
  display: inline-flex;
  align-items: center;
  opacity: 0.6;
  font-style: italic;

  &::after {
    content: '';
    margin-left: 6px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    animation: voice-transcript-pulse 1s ease-in-out infinite;
  }
}

@keyframes voice-transcript-pulse {
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
}
</style>
